import { enhancedApiClient, isSuccessResponse, ApiResponse } from "./enhancedApiClient";
import { USE_STUB_DATA, STUB_ORDERS, STUB_CUSTOMERS } from "../config/stubData";
import { Order, ItemStatus } from "../feature/orders/order";
import { orderService } from "../feature/orders/orderService";

class ApiService {
  // Order methods
  getOrders = orderService.getOrders;
  getOrder = orderService.getOrder;
  createOrder = orderService.createOrder;
  updateOrder = orderService.updateOrder;
  updateOrderStatus = orderService.updateOrderStatus;
  deleteOrder = orderService.deleteOrder;

  // Update item status
  async updateItemStatus(
    orderId: string,
    itemId: string,
    status: ItemStatus,
    stage: string
  ): Promise<ApiResponse<Order>> {
    if (USE_STUB_DATA) {
      const orderResponse = await this.getOrder(orderId);
      if (!orderResponse.success || !orderResponse.data) {
        throw new Error("Order not found");
      }
      
      const order = orderResponse.data;
      let itemFound = false;

      // Update the item status and stage
      const updatedProducts = order.products.map((product) => ({
        ...product,
        subItems: product.subItems.map((item) => {
          if (item.id === itemId) {
            itemFound = true;
            return {
              ...item,
              // We don't update status and stage for SubItems as they don't have these properties
              // Instead, we should track this in a separate mapping
            };
          }
          return item;
        }),
      }));

      if (!itemFound) {
        throw new Error("Item not found");
      }

      // Recalculate order progress
      const totalItems = updatedProducts.reduce(
        (sum, p) => sum + p.subItems.length,
        0
      );
      // For stub data, we'll simulate progress based on the stage
      const completedItems = Math.floor(
        totalItems * (stage === "dispatch" ? 1 : 0.5)
      );

      const updatedOrderResponse = await this.updateOrder(orderId, {
        products: updatedProducts,
        totalItems,
        completedItems,
        progressPercentage: Math.round((completedItems / totalItems) * 100),
        status: completedItems === totalItems ? "completed" : "in_progress",
      });

      return updatedOrderResponse;
    }
    
    const response = await enhancedApiClient.patch(
      `/orders/${orderId}/items/${itemId}`,
      { status, stage },
      {
        context: "Update Item Status",
        component: "ApiService"
      }
    );
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get order statistics
  async getOrderStatistics(): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.get("/orders/stats", {
      context: "Get Order Statistics",
      component: "ApiService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Scan QR code
  async scanQRCode(qrData: string, scannedBy: string, location?: string): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.post("/qr/scan", {
      qrData,
      scannedBy,
      location: location || "workflow_scanner"
    }, {
      context: "Scan QR Code",
      component: "ApiService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get QR codes for an order
  async getOrderQRCodes(orderId: string): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.get(`/orders/${orderId}/qr-codes`, {
      context: "Get Order QR Codes",
      component: "ApiService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get QR code for an item
  async getItemQRCode(itemId: string): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.get(`/items/${itemId}/qr-code`, {
      context: "Get Item QR Code",
      component: "ApiService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get QR code for a worker/user
  async getWorkerQRCode(userId: string): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.get(`/users/${userId}/qr-code`, {
      context: "Get Worker QR Code",
      component: "ApiService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Regenerate QR codes for an order
  async regenerateOrderQRCodes(orderId: string): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.post(`/qr/order/${orderId}/generate`, {}, {
      context: "Regenerate Order QR Codes",
      component: "ApiService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get customer statistics
  async getCustomerStatistics(): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.get("/customers/stats", {
      context: "Get Customer Statistics",
      component: "ApiService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Get system statistics
  async getSystemStatistics(): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.get("/system/stats", {
      context: "Get System Statistics",
      component: "ApiService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Export data
  async exportData(
    type: string,
    params?: any
  ): Promise<ApiResponse<any>> {
    const response = await enhancedApiClient.get(`/export/${type}`, {
      params,
      context: "Export Data",
      component: "ApiService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }

  // Import data
  async importData(
    type: string,
    file: File
  ): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append("file", file);
    
    const response = await enhancedApiClient.post(`/import/${type}`, formData, {
      context: "Import Data",
      component: "ApiService"
    });
    
    if (isSuccessResponse(response)) {
      return response;
    }
    
    return response;
  }
}

export const apiService = new ApiService();
